import React, { useCallback, useMemo } from 'react';
import { TranslateScope } from '@pegasus/translation';
import { ArticleCarousel, CardsSkeletonLoader, ArticleCompactCard, } from '@browsing/components';
import { getCatalogType, isModifierKeyPressed } from '@browsing/utils';
import { useNavigateToPDP } from '@browsing/hooks';
import { ErrorCard } from './error-card';
import { SeeAllCard } from './see-all';
var getStandardArticleCard = function (carouselItem, onClick, pdpURLPath, showStockStatus) {
    if (showStockStatus === void 0) { showStockStatus = false; }
    return (React.createElement(ArticleCompactCard, { key: carouselItem.sku, pdpURLPath: pdpURLPath, brand: carouselItem.brand, onClick: onClick, media: carouselItem.media, nameCategoryTag: carouselItem.nameCategoryTag, nameColor: carouselItem.nameColor, nameShop: carouselItem.nameShop, price: carouselItem.price, sku: carouselItem.sku, specialPrice: carouselItem.specialPrice, energyEfficiencyClass: carouselItem.energy_efficiency_class, energyLabelInfo: carouselItem.energy_label_info, stockStatus: showStockStatus ? carouselItem.stockStatus : undefined, plusBenefit: carouselItem.plusBenefit, plusEarlyAccess: carouselItem.plusEarlyAccess }));
};
var getCarouselItems = function (_a) {
    var carouselItems = _a.carouselItems, getOnArticleClick = _a.getOnArticleClick, getArticleURLPath = _a.getArticleURLPath, seeAllConfig = _a.seeAllConfig, showStockStatus = _a.showStockStatus;
    if (!(carouselItems === null || carouselItems === void 0 ? void 0 : carouselItems.length))
        return [];
    var articleCards = carouselItems.map(function (carouselItem, index) {
        return getStandardArticleCard(carouselItem, getOnArticleClick(carouselItem, index), getArticleURLPath(carouselItem, index), showStockStatus);
    });
    if (seeAllConfig === null || seeAllConfig === void 0 ? void 0 : seeAllConfig.href) {
        articleCards.push(React.createElement(SeeAllCard, { key: "see-all", href: seeAllConfig.href, image: seeAllConfig === null || seeAllConfig === void 0 ? void 0 : seeAllConfig.image, onClick: seeAllConfig === null || seeAllConfig === void 0 ? void 0 : seeAllConfig.onClick, buttontext: seeAllConfig === null || seeAllConfig === void 0 ? void 0 : seeAllConfig.buttontext, titlePosition: (seeAllConfig === null || seeAllConfig === void 0 ? void 0 : seeAllConfig.titlePosition) || 'center' }));
    }
    return articleCards;
};
export var StandardCarousel = function (_a) {
    var _b;
    var carouselItems = _a.carouselItems, loading = _a.loading, error = _a.error, tracking = _a.tracking, translationPath = _a.translationPath, title = _a.title, carouselId = _a.carouselId, seeAllConfig = _a.seeAllConfig, _c = _a.catalogType, catalogType = _c === void 0 ? (_b = getCatalogType()) !== null && _b !== void 0 ? _b : undefined : _c, themeHash = _a.themeHash, _d = _a.textTitle, textTitle = _d === void 0 ? false : _d, beforeTitle = _a.beforeTitle, afterTitle = _a.afterTitle, navigateToPDPInCurrentTab = _a.navigateToPDPInCurrentTab, _e = _a.showStockStatus, showStockStatus = _e === void 0 ? false : _e;
    var source = tracking.source, recommendedBy = tracking.recommendedBy, trackArticleSelection = tracking.trackArticleSelection, trackArticlesView = tracking.trackArticlesView, trackArticlesScroll = tracking.trackArticlesScroll;
    var _f = useNavigateToPDP({ navigateToPDPInCurrentTab: navigateToPDPInCurrentTab }), navigateToPDP = _f.navigateToPDP, getPDPURLPath = _f.getPDPURLPath;
    var getQueryParams = useCallback(function (item) { return ({
        campaignId: item.campaignIdentifier,
        configSku: item.sku,
        navigationSource: source,
        catalogType: catalogType,
        themeHash: themeHash,
        recommendedBy: recommendedBy,
    }); }, [source, catalogType, themeHash, recommendedBy]);
    var getOnArticleClick = useCallback(function (item, idx) { return function (e) {
        trackArticleSelection === null || trackArticleSelection === void 0 ? void 0 : trackArticleSelection(item, idx);
        // Prevent clicks on LUX <ArticleCard/>, whose children are wrapped in a link, from navigating to the PDP.
        e.preventDefault();
        navigateToPDP({
            queryParams: getQueryParams(item),
            shouldOpenInNewTab: isModifierKeyPressed(e),
        });
    }; }, [trackArticleSelection, navigateToPDP, getQueryParams]);
    // Pass `pdpURLPath` to <ArticleCard/>'s link for accessibility/robustness, even though we prevent its
    // default behavior. e.g. the user might right-click the link, which is not handled here.
    var getArticleURLPath = useCallback(function (item) {
        return getPDPURLPath({ queryParams: getQueryParams(item) });
    }, [getPDPURLPath, getQueryParams]);
    var handleIndexInView = useCallback(function (itemsInView) {
        if (trackArticlesView) {
            if (itemsInView.length > 0) {
                var visibleItems = carouselItems.filter(function (_, index) {
                    return itemsInView.includes(index);
                });
                trackArticlesView(visibleItems);
            }
        }
    }, [carouselItems, trackArticlesView]);
    var handleOnScroll = useCallback(function () {
        if (trackArticlesScroll) {
            trackArticlesScroll();
        }
    }, [trackArticlesScroll]);
    var memoizedCarouselItems = useMemo(function () {
        return getCarouselItems({
            carouselItems: carouselItems,
            getOnArticleClick: getOnArticleClick,
            getArticleURLPath: getArticleURLPath,
            seeAllConfig: seeAllConfig,
            showStockStatus: showStockStatus,
        });
    }, [carouselItems, getOnArticleClick, getArticleURLPath, seeAllConfig, showStockStatus]);
    if (!(carouselItems === null || carouselItems === void 0 ? void 0 : carouselItems.length) && !loading) {
        return null;
    }
    return (React.createElement(TranslateScope, { path: translationPath || '' },
        React.createElement("div", { className: "p-0" },
            loading && (React.createElement("div", { className: "py-m px-s" },
                React.createElement(CardsSkeletonLoader, { width: 185, itemsCount: 6, cardHeight: 320 }))),
            !loading && !error && (React.createElement(ArticleCarousel, { id: carouselId || 'standard-carousel', title: title, textTitle: textTitle, isOverflowCarousel: true, onSettled: handleIndexInView, onScroll: handleOnScroll, beforeTitle: beforeTitle, afterTitle: afterTitle }, memoizedCarouselItems)),
            error && React.createElement(ErrorCard, null))));
};
